import {
  createImportResult,
  FletchyVault,
  importQuiverArchive,
  parseVault,
  serializeVault,
} from "@fletchy/core";
import { firstActiveNoteId, mergeVaults } from "../state/vaultActions";

const FLETCHY_VAULT_MIME_TYPE = "application/vnd.fletchy.vault+json";

export interface VaultExportFile {
  blob: Blob;
  filename: string;
  status: string;
}

export interface VaultImportOutcome {
  vault: FletchyVault;
  importedVault: FletchyVault;
  selectedNotebookId: string | "all";
  selectedNoteId: string | null;
  importSummary: string;
  status: string;
}

function isFletchyVaultFile(file: File): boolean {
  return file.name.endsWith(".fletchy") || file.type === "application/json";
}

export function createVaultExportFile(vault: FletchyVault, exportedAt = new Date()): VaultExportFile {
  return {
    blob: new Blob([serializeVault(vault)], { type: FLETCHY_VAULT_MIME_TYPE }),
    filename: `fletchy-${exportedAt.toISOString().slice(0, 10)}.fletchy`,
    status: "Exported .fletchy vault",
  };
}

export async function importVaultFile(currentVault: FletchyVault, file: File): Promise<VaultImportOutcome> {
  if (isFletchyVaultFile(file)) {
    const importedVault = parseVault(await file.text());
    const result = createImportResult({
      notebooks: importedVault.notebooks.length,
      notes: importedVault.notes.length,
      cells: importedVault.cells.length,
    });

    return {
      vault: importedVault,
      importedVault,
      selectedNotebookId: importedVault.notebooks[0]?.id ?? "all",
      selectedNoteId: firstActiveNoteId(importedVault),
      importSummary: `Imported ${result.importedCounts.notes ?? 0} notes from .fletchy`,
      status: "Imported .fletchy vault",
    };
  }

  const { vault: importedVault, result } = await importQuiverArchive(file);
  const merged = mergeVaults(currentVault, importedVault);

  return {
    vault: merged,
    importedVault,
    selectedNotebookId: importedVault.notebooks[0]?.id ?? "all",
    selectedNoteId: firstActiveNoteId(importedVault),
    importSummary: `Imported ${result.importedCounts.notes ?? 0} notes, ${result.importedCounts.cells ?? 0} cells. ${
      result.warnings[0] ?? ""
    }`,
    status: "Imported Quiver archive",
  };
}
